import Phaser from 'phaser';
import { APP_CONFIG } from '../../app/config';
import { AppState } from '../../domain/state/AppState';
import type { GameStateMachine } from '../../domain/state/GameStateMachine';
import type { KeyboardInputAdapter } from '../../input/KeyboardInputAdapter';
import { introVisualState } from '../../intro/trajectory';
import type { AudioSystem } from '../../services/AudioSystem';
import type { EventBus } from '../../services/EventBus';
import type { RandomSource } from '../../services/RandomSource';
import type { StorageService } from '../../services/StorageService';
import { GameWorld } from '../systems/GameWorld';
import type { GameWorldSnapshot } from '../systems/GameWorld';
import type { PowerUpType } from '../../domain/powerups/PowerUpSpawnManager';
import {
  SpaceBackgroundController,
  type SpaceBackgroundDebug,
} from '../background/SpaceBackgroundController';

type SceneDependencies = {
  machine: GameStateMachine;
  events: EventBus;
  input: KeyboardInputAdapter;
  storage: StorageService;
  audio: AudioSystem;
  random: RandomSource;
  highScore: number;
  touch: { axis: { x: number; y: number }; fire: boolean };
  onReady: (scene: ExperienceScene) => void;
};

export type ShipVisualState = {
  active: boolean;
  visible: boolean;
  alpha: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
};

export type IntroSceneVisualDebug = {
  progress: number;
  ship: ShipVisualState & { x: number; y: number; depth: number };
  ground: { y: number; visible: boolean; alpha: number; depth: number };
  mountains: { y: number; visible: boolean; alpha: number; depth: number };
  sky: { visible: boolean; alpha: number; depth: number };
  spaceBackground: SpaceBackgroundDebug;
  camera: { scrollX: number; scrollY: number; backgroundColor: number };
};

export class ExperienceScene extends Phaser.Scene {
  private sky!: Phaser.GameObjects.Graphics;
  private planet!: Phaser.GameObjects.Arc;
  private mountains!: Phaser.GameObjects.Graphics;
  private ground!: Phaser.GameObjects.Container;
  private ship!: Phaser.GameObjects.Container;
  private flame!: Phaser.GameObjects.Graphics;
  private world!: GameWorld;
  private background!: SpaceBackgroundController;
  private introProgress = 0;

  public constructor(private readonly dependencies: SceneDependencies) {
    super('experience');
  }

  public create(): void {
    this.cameras.main.setBackgroundColor('#050714');
    this.sky = this.add.graphics().setDepth(0);
    this.background = new SpaceBackgroundController(this);
    this.planet = this.add.circle(240, 510, 260, 0x15254d).setDepth(4);
    this.mountains = this.add.graphics().setDepth(5);
    this.drawMountains();
    this.ground = this.createGround();
    this.ship = this.createShip();
    this.world = new GameWorld(
      this,
      this.ship,
      this.dependencies.input,
      this.dependencies.touch,
      this.dependencies.machine,
      this.dependencies.events,
      this.dependencies.storage,
      this.dependencies.audio,
      this.dependencies.random,
      this.dependencies.highScore,
    );
    this.renderIntroProgress(0);
    this.events.once('shutdown', () => this.background.destroy());
    this.dependencies.onReady(this);
  }

  public update(time: number, delta: number): void {
    const state = this.dependencies.machine.state;
    const backgroundSpeed = [AppState.Playing, AppState.Hit].includes(state) ? 92 : 10;
    this.background.update(delta, backgroundSpeed);
    const flameFrame = Math.floor(time / 75) % 3;
    this.flame.scaleX = 0.72 + this.introProgress * 0.45 + flameFrame * 0.12;
    this.world.update(delta);
  }

  public renderIntroProgress(value: number): void {
    const visual = introVisualState(value, APP_CONFIG.baseWidth, APP_CONFIG.baseHeight);
    this.introProgress = visual.progress;
    const progress = visual.progress;
    this.drawSky(progress);
    this.ship
      .setActive(true)
      .setVisible(true)
      .setAlpha(1)
      .setPosition(Math.round(visual.ship.x), Math.round(visual.ship.y))
      .setRotation(visual.ship.rotation);
    this.ground.setPosition(0, Math.round(visual.groundY)).setAlpha(visual.groundAlpha);
    this.mountains.setPosition(0, Math.round(visual.mountainsY)).setAlpha(visual.mountainsAlpha);
    this.planet.setPosition(240, visual.planetY).setAlpha(visual.planetAlpha);
    this.background.setIntroProgress(progress);
    this.flame.scaleX = 0.7 + Math.sin(progress * 70) * 0.08 + progress * 0.55;
    this.dependencies.events.emit('intro:progress', { progress });
  }

  public setIntroProgress(value: number): void {
    this.renderIntroProgress(value);
  }

  public startGame(): void {
    this.renderIntroProgress(1);
    this.resetShipVisual(140, 135);
    this.world.start();
  }

  public resetGame(): void {
    this.world.reset();
    this.resetShipVisual(140, 135);
  }

  public resetDemo(): void {
    this.world.reset();
    this.background.reset();
    this.resetShipVisual(58, 210);
    this.renderIntroProgress(0);
  }

  public get debugSnapshot(): GameWorldSnapshot {
    return this.world.snapshot;
  }

  public get currentIntroProgress(): number {
    return this.introProgress;
  }

  public get currentShipPosition(): { x: number; y: number } {
    return { x: this.ship.x, y: this.ship.y };
  }

  public get currentShipVisualState(): ShipVisualState {
    return {
      active: this.ship.active,
      visible: this.ship.visible,
      alpha: this.ship.alpha,
      scaleX: this.ship.scaleX,
      scaleY: this.ship.scaleY,
      rotation: this.ship.rotation,
    };
  }

  public get backgroundDebug(): SpaceBackgroundDebug {
    return this.background.debug;
  }

  public get introVisualDebug(): IntroSceneVisualDebug {
    return {
      progress: this.introProgress,
      ship: {
        ...this.currentShipVisualState,
        x: this.ship.x,
        y: this.ship.y,
        depth: this.ship.depth,
      },
      ground: {
        y: this.ground.y,
        visible: this.ground.visible,
        alpha: this.ground.alpha,
        depth: this.ground.depth,
      },
      mountains: {
        y: this.mountains.y,
        visible: this.mountains.visible,
        alpha: this.mountains.alpha,
        depth: this.mountains.depth,
      },
      sky: {
        visible: this.sky.visible,
        alpha: this.sky.alpha,
        depth: this.sky.depth,
      },
      spaceBackground: this.background.debug,
      camera: {
        scrollX: this.cameras.main.scrollX,
        scrollY: this.cameras.main.scrollY,
        backgroundColor: this.cameras.main.backgroundColor.color,
      },
    };
  }

  public damagePlayerForTest(): void {
    this.world.damageForTest();
  }

  public gameOverForTest(): void {
    this.world.gameOverForTest();
  }

  public setAmmoForTest(ammo: number): void {
    this.world.setAmmoForTest(ammo);
  }

  public spawnPowerUpForTest(type: PowerUpType): void {
    this.world.spawnPowerUpForTest(type);
  }

  public collectPowerUpForTest(type: PowerUpType): void {
    this.world.collectPowerUpForTest(type);
  }

  public toggleHitboxesForDebug(): boolean {
    return this.world.toggleDebugHitboxes();
  }

  private drawSky(progress: number): void {
    this.sky.clear();
    const layers = [0x101947, 0x0b1235, 0x070b22, 0x050714];
    const offset = Math.min(3, Math.floor(progress * 4));
    for (let index = 0; index < 4; index += 1) {
      this.sky.fillStyle(layers[Math.min(3, index + offset)] ?? 0x050714);
      this.sky.fillRect(0, index * 68, APP_CONFIG.baseWidth, 68);
    }
  }

  private drawMountains(): void {
    this.mountains.fillStyle(0x121b3c);
    this.mountains.fillTriangle(0, 215, 105, 124, 210, 215);
    this.mountains.fillTriangle(120, 215, 290, 140, 410, 215);
    this.mountains.fillTriangle(300, 215, 430, 116, 520, 215);
    this.mountains.fillStyle(0x26365a);
    this.mountains.fillTriangle(70, 215, 175, 156, 280, 215);
  }

  private createGround(): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0).setDepth(7);
    const soil = this.add.rectangle(240, 249, 480, 54, 0x4b2d45);
    const lower = this.add.rectangle(240, 263, 480, 26, 0x2d2038);
    const grass = this.add.rectangle(240, 220, 480, 6, 0x73c75d);
    container.add([soil, lower, grass]);
    for (let x = 5; x < 480; x += 14) {
      container.add(this.add.rectangle(x, 226 + ((x / 14) % 3) * 7, 5, 3, 0x75504f));
    }
    return container;
  }

  private createShip(): Phaser.GameObjects.Container {
    const container = this.add.container(58, 210).setDepth(18);
    this.flame = this.add.graphics();
    this.flame.fillStyle(0xff5f45).fillTriangle(-37, 0, -25, -6, -25, 6);
    this.flame.fillStyle(0xffb24b).fillTriangle(-34, 0, -24, -4, -24, 4);
    this.flame.fillStyle(0xfff0a3).fillRect(-29, -2, 7, 4);
    const body = this.add.graphics();
    body.fillStyle(0x292d3b).fillRect(-27, -6, 6, 12);
    body.fillStyle(0x555c67).fillRect(-25, -4, 4, 8);
    body.fillStyle(0x252a36).fillRect(-22, -10, 24, 20);
    body.fillStyle(0x8e969d).fillRect(-20, -8, 20, 16);
    body.fillStyle(0xc8ced0).fillRect(-19, -7, 18, 5);
    body.fillStyle(0x59616b).fillRect(-19, 3, 18, 4);
    body.fillStyle(0x353b47).fillRect(-13, -8, 2, 16);
    body.fillStyle(0x353b47).fillRect(-5, -8, 2, 16);
    body.fillStyle(0x65c9d2).fillRect(-18, -5, 3, 3);
    body.fillStyle(0x242936).fillTriangle(-2, -11, 28, 0, -2, 11);
    body.fillStyle(0xa7afb2).fillTriangle(0, -8, 24, 0, 0, 8);
    body.fillStyle(0xe1e5df).fillTriangle(1, -7, 19, -1, 1, -1);
    body.fillStyle(0x59616b).fillTriangle(0, 2, 21, 1, 0, 8);
    body.fillStyle(0x182633).fillTriangle(5, -6, 13, -3, 5, -2);
    body.fillStyle(0x55d9e8).fillRect(7, -4, 3, 2);
    body.fillStyle(0x303641).fillRect(-2, -9, 2, 18);
    body.fillStyle(0xdce2df).fillRect(23, -1, 4, 2);
    container.add([this.flame, body]);
    return container;
  }

  private resetShipVisual(x: number, y: number): void {
    this.ship
      .setActive(true)
      .setVisible(true)
      .setAlpha(1)
      .setScale(1)
      .setRotation(0)
      .setPosition(x, y);
    this.flame.setVisible(true).setAlpha(1).setScale(1);
  }
}
